import { forwardRef, useEffect, useState } from "react";
import { ApiError, formatSupportRef } from "./api";

/**
 * Polished error banner — Stripe / Vercel / Linear grade.
 *
 * Three visual states driven by `kind`:
 *   • destructive (auth / validation) — deep pink tint, alert icon
 *   • warn        (rate-limit)        — amber tint, clock icon +
 *                                        live-updating countdown
 *   • info        (network / offline) — blue-grey tint, wifi icon
 *
 * Below the main message, a tiny muted ref (`INS-429 · 90a1b2c3`) so
 * a user reporting a stuck sign-in can paste something grep-able into
 * a support ticket. The `requestId` is the CF `cf-ray` header on the
 * failing response.
 *
 * `role="alert"` announces on mount to AT users; the `tabIndex={-1}`
 * + forwarded ref lets the parent screen focus the banner on submit
 * failure so screen readers and keyboard users don't miss it.
 */

interface BaseProps {
    message: string;
    /** Short code + request id for support; hidden when no code. */
    supportRef?: string;
    /** "destructive" (default), "warn", "info". */
    tone?: "destructive" | "warn" | "info";
    /** Live countdown seconds — renders under the message. */
    countdownSeconds?: number | null;
}

export const ErrorDisplay = forwardRef<HTMLDivElement, BaseProps>(function ErrorDisplay(
    { message, supportRef, tone = "destructive", countdownSeconds },
    ref,
) {
    return (
        <div
            ref={ref}
            tabIndex={-1}
            role="alert"
            aria-live="assertive"
            className={`err err--${tone}`}
        >
            <span className="err-icon" aria-hidden="true">
                <ErrorIcon tone={tone} />
            </span>
            <div className="err-body">
                <p className="err-message">{message}</p>
                {countdownSeconds != null && countdownSeconds > 0 && (
                    <p className="err-countdown">
                        Try again in <strong>{countdownSeconds}s</strong>
                    </p>
                )}
                {supportRef && <p className="err-ref">{supportRef}</p>}
            </div>
        </div>
    );
});

/**
 * Convenience wrapper — takes an ApiError directly and picks the right
 * tone, countdown, and supportRef for its kind. Use this instead of
 * the raw ErrorDisplay when the source is an API failure; the plain
 * component is for client-side validation errors (password mismatch,
 * etc.) that don't have an ApiError backing them.
 */
export const ApiErrorDisplay = forwardRef<HTMLDivElement, {
    error: ApiError;
    /** Override the default message — e.g. auth screens say "Invalid
     * credentials." instead of the generic category text. */
    messageOverride?: string;
}>(function ApiErrorDisplay({ error, messageOverride }, ref) {
    const tone: BaseProps["tone"] =
        error.kind === "rate-limit" ? "warn"
        : error.kind === "network" ? "info"
        : "destructive";
    const countdown = useRateLimitCountdown(
        error.kind === "rate-limit" ? error.retryAfterSeconds : null,
    );
    return (
        <ErrorDisplay
            ref={ref}
            tone={tone}
            message={messageOverride ?? error.message}
            supportRef={formatSupportRef(error)}
            countdownSeconds={countdown}
        />
    );
});

/**
 * Drives the "Try again in Xs" counter. Ticks once per second, stops
 * at 0. Returns null when `initial` is null so the countdown line is
 * omitted entirely.
 */
export function useRateLimitCountdown(initial: number | null): number | null {
    const [remaining, setRemaining] = useState<number | null>(initial);

    useEffect(() => {
        setRemaining(initial);
        if (initial == null || initial <= 0) return;
        const interval = setInterval(() => {
            setRemaining((current) => {
                if (current == null || current <= 1) {
                    clearInterval(interval);
                    return 0;
                }
                return current - 1;
            });
        }, 1000);
        return () => clearInterval(interval);
    }, [initial]);

    return remaining;
}

function ErrorIcon({ tone }: { tone: BaseProps["tone"] }) {
    // Three hand-drawn line icons — consistent 16×16, stroke-based so
    // they inherit colour from the parent's CSS `color` property.
    const common = {
        width: 16,
        height: 16,
        viewBox: "0 0 16 16",
        fill: "none",
        stroke: "currentColor",
        strokeWidth: 1.5,
        strokeLinecap: "round" as const,
        strokeLinejoin: "round" as const,
    };
    if (tone === "warn") {
        // Clock
        return (
            <svg {...common}>
                <circle cx="8" cy="8" r="6.25" />
                <path d="M8 4.5V8l2.25 1.5" />
            </svg>
        );
    }
    if (tone === "info") {
        // Wifi-off / disconnect — stylised radio-wave stack
        return (
            <svg {...common}>
                <path d="M2 6.5a9 9 0 0 1 12 0" />
                <path d="M4.5 9a5.5 5.5 0 0 1 7 0" />
                <circle cx="8" cy="12" r="0.75" fill="currentColor" stroke="none" />
                <path d="M1.5 14.5 14.5 1.5" />
            </svg>
        );
    }
    // destructive — alert circle
    return (
        <svg {...common}>
            <circle cx="8" cy="8" r="6.25" />
            <path d="M8 5v3.25" />
            <circle cx="8" cy="11" r="0.75" fill="currentColor" stroke="none" />
        </svg>
    );
}
